'use client'

import '@chain-zen/ui/styles/globals.css'

import * as Sentry from '@sentry/browser'
import { useEffect } from 'react'

import { geistMono, geistSans } from '../lib/fonts'

const isDev = process.env.NODE_ENV === 'development'

const INBOX_ROUTE = '/user/inbox'

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  useEffect(() => {
    Sentry.captureException(error, {
      extra: {
        digest: error.digest,
        errorMessage: error.message,
        errorName: error.name,
        stack: error.stack,
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : 'SSR',
        userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'SSR'
      },
      tags: {
        errorType: 'global-error',
        page: typeof window !== 'undefined' ? window.location.pathname : 'unknown'
      }
    })

    console.error('Global Error Caught:', {
      message: error.message,
      name: error.name,
      digest: error.digest,
      stack: error.stack,
      url: typeof window !== 'undefined' ? window.location.href : 'SSR'
    })
  }, [error])

  return (
    <html lang="en" suppressHydrationWarning={true} className="antialiased">
      <body className={`min-h-svh font-sans ${geistSans.variable} ${geistMono.variable}`}>
        <div className="flex min-h-screen w-full flex-col items-center justify-center bg-background px-4">
          <div className="flex max-w-md flex-col items-center text-center">
            <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-destructive"
              >
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
              </svg>
            </div>

            <h1 className="mb-2 text-xl font-semibold tracking-tight text-foreground">Something went wrong</h1>
            <p className="mb-8 text-sm leading-relaxed text-muted-foreground">
              An unexpected error occurred. Our team has been notified and is looking into it.
            </p>

            {!!isDev && (
              <div className="mb-8 w-full rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-left">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-destructive">
                  Debug Info (dev only)
                </p>
                <p className="mb-1 text-sm text-foreground">
                  <span className="font-medium">Error:</span> {error.name}
                </p>
                <p className="mb-1 text-sm text-foreground">
                  <span className="font-medium">Message:</span> {error.message}
                </p>
                {!!error.digest && (
                  <p className="mb-1 text-sm text-foreground">
                    <span className="font-medium">Digest:</span> {error.digest}
                  </p>
                )}
                {!!error.stack && (
                  <pre className="mt-3 max-h-48 overflow-auto rounded-md bg-muted p-3 font-mono text-xs text-muted-foreground">
                    {error.stack}
                  </pre>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={reset}
                className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Try again
              </button>
              <button
                onClick={() => {
                  window.location.href = INBOX_ROUTE
                }}
                className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                Go to Inbox
              </button>
            </div>
          </div>
        </div>
      </body>
    </html>
  )
}
